import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { MDButtonOwnerState } from "./types";
import { 
  pxToRem,
  rgba,
  boxShadow,
  linearGradient,
  getButtonThemeColors,
  getButtonGradients,
  getButtonColorConfig,
  getColorValue
} from "../../../../theme/utils/componentUtils";

interface MDButtonRootProps {
  ownerState: MDButtonOwnerState;
}

export default styled(Button)<MDButtonRootProps>(({ theme, ownerState }) => {
  const { palette, spacing } = theme;
  const { color, variant, size, circular, iconOnly, darkMode } = ownerState;

  // Get theme-aware color and gradient definitions
  const colors = getButtonThemeColors(palette);
  const gradients = getButtonGradients(palette);
  const colored = getButtonColorConfig();
  const borderRadius = { section: spacing(2) };


  // styles for the button with variant="contained"
  const containedStyles = () => {
    const colorObj = getColorValue(palette, color);
    const backgroundValue = colorObj.main;
    const focusedBackgroundValue = colorObj.focus || colorObj.main;

    // boxShadow value
    const boxShadowValue = colored[color as keyof typeof colored]
      ? `${boxShadow([0, 3], [3, 0], backgroundValue, 0.15)}, ${boxShadow(
          [0, 3],
          [1, -2],
          backgroundValue,
          0.2
        )}, ${boxShadow([0, 1], [5, 0], backgroundValue, 0.15)}`
      : "none";

    // boxShadow value when button is hovered
    const hoveredBoxShadowValue = colored[color as keyof typeof colored]
      ? `${boxShadow([0, 14], [26, -12], backgroundValue, 0.4)}, ${boxShadow(
          [0, 4],
          [23, 0],
          backgroundValue,
          0.15
        )}, ${boxShadow([0, 8], [10, -5], backgroundValue, 0.2)}`
      : "none";

    // color value
    let colorValue = colors.white.main;

    if (!darkMode && (color === "white" || color === "light")) {
      colorValue = colors.text.main;
    } else if (darkMode && (color === "white" || color === "light")) {
      colorValue = colors.grey[600];
    }

    // color value when button is focused
    let focusedColorValue = colors.white.main;

    if (color === "white") {
      focusedColorValue = colors.text.main;
    } else if (color === "primary" || color === "error" || color === "dark") {
      focusedColorValue = colors.white.main;
    }

    return {
      background: backgroundValue,
      color: colorValue,
      boxShadow: boxShadowValue,

      "&:hover": {
        backgroundColor: backgroundValue,
        boxShadow: hoveredBoxShadowValue,
      },

      "&:focus:not(:hover)": {
        backgroundColor: focusedBackgroundValue,
        boxShadow: boxShadow([0, 0], [0, 3.2], backgroundValue, 0.5),
      },

      "&:disabled": {
        backgroundColor: backgroundValue,
        color: focusedColorValue,
      },
    };
  };

  // styles for the button with variant="outlined"
  const outliedStyles = () => {
    const colorObj = getColorValue(palette, color);
    
    // background color value
    const backgroundValue = color === "white" ? rgba(colors.white.main, 0.1) : colors.transparent.main;

    // color value
    const colorValue = colorObj.main;

    // boxShadow value
    const boxShadowValue = boxShadow([0, 0], [0, 3.2], colorObj.main, 0.5);

    // border color value
    let borderColorValue = colorObj.main;

    if (color === "white") {
      borderColorValue = rgba(colors.white.main, 0.75);
    }

    return {
      background: backgroundValue,
      color: colorValue,
      borderColor: borderColorValue,

      "&:hover": {
        background: colors.transparent.main,
        borderColor: colorValue,
      },

      "&:focus:not(:hover)": {
        background: colors.transparent.main,
        boxShadow: boxShadowValue,
      },

      "&:active:not(:hover)": {
        backgroundColor: colorValue,
        color: colors.white.main,
        opacity: 0.85,
      },

      "&:disabled": {
        color: colorValue,
        borderColor: colorValue,
      },
    };
  };

  // styles for the button with variant="gradient"
  const gradientStyles = () => {
    const gradientObj = gradients[color as keyof typeof gradients];
    
    // background value
    const backgroundValue =
      color === "white" || !gradientObj
        ? colors.white.main
        : linearGradient(gradientObj.main, gradientObj.state);

    // boxShadow value
    const colorObj = getColorValue(palette, color);
    const boxShadowValue = colored[color as keyof typeof colored]
      ? `${boxShadow([0, 3], [3, 0], colorObj.main, 0.15)}, ${boxShadow(
          [0, 3],
          [1, -2],
          colorObj.main,
          0.2
        )}, ${boxShadow([0, 1], [5, 0], colorObj.main, 0.15)}`
      : "none";

    // boxShadow value when button is hovered
    const hoveredBoxShadowValue = colored[color as keyof typeof colored]
      ? `${boxShadow([0, 14], [26, -12], colorObj.main, 0.4)}, ${boxShadow(
          [0, 4],
          [23, 0],
          colorObj.main,
          0.15
        )}, ${boxShadow([0, 8], [10, -5], colorObj.main, 0.2)}`
      : "none";

    // color value
    let colorValue = colors.white.main;

    if (color === "white") {
      colorValue = colors.text.main;
    } else if (color === "light") {
      colorValue = gradients.dark.state;
    }

    return {
      background: backgroundValue,
      color: colorValue,
      boxShadow: boxShadowValue,

      "&:hover": {
        boxShadow: hoveredBoxShadowValue,
      },

      "&:focus:not(:hover)": {
        boxShadow: boxShadowValue,
      },

      "&:disabled": {
        background: backgroundValue,
        color: colorValue,
      },
    };
  };

  // styles for the button with variant="text"
  const textStyles = () => {
    const colorObj = getColorValue(palette, color);
    
    // color value
    const colorValue = colorObj.main;

    // color value when button is focused
    const focusedColorValue = colorObj.focus || colorObj.main;

    return {
      color: colorValue,

      "&:hover": {
        color: focusedColorValue,
      },

      "&:focus:not(:hover)": {
        color: focusedColorValue,
      },
    };
  };

  // styles for the button with circular={true}
  const circularStyles = () => ({
    borderRadius: borderRadius.section,
  });

  // styles for the button with iconOnly={true}
  const iconOnlyStyles = () => {
    // width, height, minWidth and minHeight values
    let sizeValue = pxToRem(38);

    if (size === "small") {
      sizeValue = pxToRem(25.4);
    } else if (size === "large") {
      sizeValue = pxToRem(52);
    }

    // padding value
    let paddingValue = `${pxToRem(11)} ${pxToRem(11)} ${pxToRem(10)}`;

    if (size === "small") {
      paddingValue = pxToRem(4.5);
    } else if (size === "large") {
      paddingValue = pxToRem(16);
    }

    return {
      width: sizeValue,
      minWidth: sizeValue,
      height: sizeValue,
      minHeight: sizeValue,
      padding: paddingValue,

      "& .material-icons": {
        marginTop: 0,
      },

      "&:hover, &:focus, &:active": {
        transform: "none",
      },
    };
  };

  return {
    ...(variant === "contained" && containedStyles()),
    ...(variant === "outlined" && outliedStyles()),
    ...(variant === "gradient" && gradientStyles()),
    ...(variant === "text" && textStyles()),
    ...(circular && circularStyles()),
    ...(iconOnly && iconOnlyStyles()),
  };
});